import React from 'react';
import { useTranslation } from 'react-i18next';

import { LabelInput } from '@/components/ui/inputs/LabelInput';
import { Text } from '@/components/ui/text/Text';
import { SidePanelFormLayout } from '@/components/layout';
import { colors } from '@/assets/styles/colors';
import type { PaymentConceptItem } from '@/api/domains/payment';


export interface PaymentConceptErrorDetailPanelProps {
  record: PaymentConceptItem | null;
}

/**
 * Panel lateral de solo lectura con el detalle de un error de envío de un concepto de pago
 * (ASIS-5318). Presentacional puro: recibe el registro ya mapeado por la grilla, sin llamadas
 * API ni estado propio.
 */
export const PaymentConceptErrorDetailPanel: React.FC<PaymentConceptErrorDetailPanelProps> = ({
  record,
}) => {
  const { t: translate } = useTranslation('payment');

  const errorMessages = record?.errorMessages ?? [];

  return (
    <SidePanelFormLayout data-testid="payment-concept-error-detail-panel">
      <SidePanelFormLayout.Fields>
        <LabelInput
          label={translate('list.errorDetail.rut')}
          value={record?.employeeTaxIdentificationNumber ?? ''}
          readOnly
        />
        <LabelInput
          label={translate('list.errorDetail.collaborator')}
          value={record?.employeeName ?? ''}
          readOnly
        />
        <LabelInput
          label={translate('list.errorDetail.conceptId')}
          value={record?.concept ?? ''}
          readOnly
        />
        <LabelInput
          label={translate('list.errorDetail.concept')}
          value={record?.conceptName ?? ''}
          readOnly
        />
        <LabelInput
          label={translate('list.errorDetail.value')}
          value={record?.value ?? ''}
          readOnly
        />
        <LabelInput
          label={translate('list.errorDetail.status')}
          value={record?.status ?? ''}
          readOnly
        />
      </SidePanelFormLayout.Fields>

      <div className="w-full flex flex-col gap-2">
        <Text variant="subheader-medium" color={colors.grays.neutral33}>
          {translate('list.errorDetail.detail')}
        </Text>
        <div
          className="rounded p-3 max-h-64 overflow-y-auto"
          style={{ backgroundColor: colors.grays.neutralFA }}
          data-testid="payment-concept-error-detail-messages"
        >
          {errorMessages.length === 1 && (
            <Text variant="body-regular" color={colors.grays.neutral33} className="break-words">
              {errorMessages[0]}
            </Text>
          )}
          {errorMessages.length > 1 && (
            <ul className="list-disc pl-4 flex flex-col gap-1">
              {errorMessages.map((message) => (
                <li key={message} className="break-words">
                  <Text as="span" variant="body-regular" color={colors.grays.neutral33} className="break-words">
                    {message}
                  </Text>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </SidePanelFormLayout>
  );
};

export default PaymentConceptErrorDetailPanel;
